import { forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VERIFY_BASE = typeof window !== "undefined" ? window.location.origin : BACKEND_URL;

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", { 
    day: "numeric", 
    month: "long", 
    year: "numeric" 
  });
};

// Calculate credits - static values for maximum possible credits per level
const calculateMaxCredits = (level) => {
  const credits = { diploma: 60, bachelor: 120, master: 180, doctoral: 240 };
  return credits[level] || 60;
};

// Calculate actual earned credits from scored competencies
const calculateEarnedCredits = (competencies, scores, level) => {
  // Filter competencies for the given level
  const levelCompetencies = competencies.filter(c => c.qualification_level === level);
  
  // Sum credits for competencies that have passing scores (> 0)
  let earnedCredits = 0;
  levelCompetencies.forEach(comp => {
    const score = scores.find(s => s.competency_id === comp.competency_id);
    if (score && score.assessor_score > 0) {
      earnedCredits += comp.credit_hours || 4; // Default 4 credits per competency
    }
  });
  
  return earnedCredits;
};

const getLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

// Compose "Full Name (SHORT)" from an IPCB / Institution record. Falls back
// gracefully so legacy records without short_name still display correctly.
// We deliberately ignore `board_name` for the headline — historically it
// held a generic descriptor like "Industrial Professional Competency Board
// (IPCB)" which is misleading on a candidate's official report.
const formatOrgName = (org, fallback = "") => {
  if (!org) return fallback;
  const full = (org.name || "").trim();
  const short = (org.short_name || "").trim();
  if (full && short && !full.toUpperCase().includes(short.toUpperCase())) {
    return `${full} (${short})`;
  }
  return full || short || fallback;
};

// Page Header Component
const PageHeader = ({ ipcb, pageNum, totalPages }) => (
  <div style={{ 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
    borderBottom: "3px solid #1e3a5f", 
    paddingBottom: "15px", 
    marginBottom: "25px" 
  }}>
    <div>
      <h1 style={{ fontSize: "16pt", fontWeight: "bold", color: "#1e3a5f", margin: "0" }}>
        CANDIDATE EVALUATION REPORT
      </h1>
      <p style={{ fontSize: "11pt", color: "#666", margin: "5px 0 0 0" }}>
        {formatOrgName(ipcb, "Awarding Body")}
      </p>
    </div>
    {ipcb?.logo_url && (
      <img src={getLogoUrl(ipcb.logo_url)} alt={ipcb.name} crossOrigin="anonymous" style={{ height: "55px", width: "auto", maxWidth: "180px", objectFit: "contain" }} />
    )}
  </div>
);

// Page Footer Component
const PageFooter = ({ candidate, evalDate, pageNum, totalPages, institution }) => (
  <div style={{ 
    borderTop: "2px solid #1e3a5f", 
    paddingTop: "15px", 
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }}>
    <p style={{ fontSize: "10pt", color: "#666", margin: "0", fontFamily: "Courier New, monospace" }}>
      Ref: {candidate?.ipcb_report_id || `IPCB-${candidate?.candidate_id?.slice(-8)?.toUpperCase() || "XXXXXXXX"}`}
    </p>
    <p style={{ fontSize: "10pt", color: "#666", margin: "0" }}>
      To be submitted to: {formatOrgName(institution, "Awarding Institution")}
    </p>
    <p style={{ fontSize: "10pt", color: "#666", margin: "0" }}>
      Page {pageNum} of {totalPages}
    </p>
  </div>
);

// Main IPCB Report - 3 Pages with proper spacing
export const IPCBReport = forwardRef(({ 
  candidate, 
  ipcb, 
  institution, 
  documents = [],
  scores = [],
  competencies = [],
  aiRecommendation,
}, ref) => {
  const level = aiRecommendation?.recommended_level || candidate?.assessor_decision?.final_level || "diploma";
  const maxCredits = calculateMaxCredits(level);
  const earnedCredits = calculateEarnedCredits(competencies, scores, level);
  // Use earned credits, or fall back to counting competencies * 4 if no scores yet
  const credits = earnedCredits > 0 ? earnedCredits : (competencies.filter(c => c.qualification_level === level).length * 4);
  const evalDate = formatDate(candidate?.updated_at || new Date().toISOString());
  const proposedAward = candidate?.final_award || aiRecommendation?.primary_award || "Pending Selection";
  
  const relevanceSummary = aiRecommendation?.relevance_summary || 
    `${candidate?.full_name} has ${candidate?.years_experience || "several"} years of professional experience${candidate?.current_role ? ` as a ${candidate.current_role}` : ""}${candidate?.current_employer ? ` at ${candidate.current_employer}` : ""}. The submitted evidence demonstrates competencies aligned with the proposed qualification.`;

  const assessorComments = candidate?.assessor_decision?.assessor_comments || 
    `Based on comprehensive review of submitted documentation, ${candidate?.full_name} demonstrates competencies aligned with the ${proposedAward} qualification through documented professional experience.`;

  const competenciesList = aiRecommendation?.demonstrated_competencies || [
    "Applied technical skills in professional domain",
    "Workplace coordination and collaboration",
    "Resource planning and efficiency",
    "Compliance with industry standards",
    "Project execution and scheduling",
    "Quality assurance and monitoring",
    "Problem-solving in work contexts",
    "Professional ethics and discipline"
  ];

  const docsList = documents.length > 0 ? documents : [
    { category: "RPL Application Form" },
    { category: "Curriculum Vitae / Resume" },
    { category: "Employment Verification Letters" },
    { category: "Professional Certificates" },
    { category: "Work Samples / Portfolio" },
    { category: "Supporting Evidence" }
  ];

  const pageStyle = {
    fontFamily: "Tahoma, Arial, sans-serif",
    fontSize: "12pt",
    lineHeight: "1.6",
    color: "#1a1a1a",
    padding: "40px 50px",
    minHeight: "1100px",
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
  };

  const sectionTitle = {
    fontSize: "14pt",
    fontWeight: "bold",
    color: "#1e3a5f",
    borderBottom: "2px solid #e5e7eb",
    paddingBottom: "8px",
    marginTop: "25px",
    marginBottom: "15px",
  };

  const labelStyle = {
    fontSize: "10pt",
    fontWeight: "bold",
    color: "#666",
    margin: "0 0 3px 0",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const valueStyle = {
    fontSize: "12pt",
    color: "#1a1a1a",
    margin: "0",
    lineHeight: "1.5",
  };

  return (
    <div ref={ref} style={{ backgroundColor: "white" }} data-testid="ipcb-report">
      
      {/* PAGE 1 - Cover & Candidate Information */}
      <div data-report-page="ipcb-1" style={{ ...pageStyle, pageBreakAfter: "always" }}>
        {/* Main Header for Page 1 */}
        <div style={{ textAlign: "center", borderBottom: "4px solid #1e3a5f", paddingBottom: "25px", marginBottom: "30px", position: "relative" }}>
          {candidate?.ipcb_report_id && (
            <div style={{
              position: "absolute", top: "0", right: "0",
              backgroundColor: "#1e3a5f", color: "white",
              padding: "6px 14px", borderRadius: "0 0 0 8px",
              fontSize: "10pt", fontFamily: "Courier New, monospace",
              fontWeight: "bold", letterSpacing: "1px",
            }}>
              Report ID: {candidate.ipcb_report_id}
            </div>
          )}
          {ipcb?.logo_url && (
            <img src={getLogoUrl(ipcb.logo_url)} alt={ipcb.name} crossOrigin="anonymous" style={{ height: "70px", width: "auto", maxWidth: "220px", objectFit: "contain", marginBottom: "20px" }} />
          )}
          <h1 style={{ fontSize: "20pt", fontWeight: "bold", color: "#1e3a5f", margin: "0 0 8px 0" }}>
            CANDIDATE EVALUATION REPORT
          </h1>
          <h2 style={{ fontSize: "14pt", fontWeight: "normal", color: "#2563eb", margin: "0 0 12px 0" }}>
            FOR RPL AWARDS
          </h2>
          <p style={{ fontSize: "12pt", color: "#333", margin: "0" }}>
            Prepared by: {formatOrgName(ipcb, "Awarding Body")}
          </p>
        </div>

        {/* Candidate Information */}
        <h2 style={sectionTitle}>Candidate Information</h2>
        
        <table style={{ width: "100%", marginBottom: "25px" }}>
          <tbody>
            <tr>
              <td style={{ padding: "12px 20px 12px 0", width: "50%", verticalAlign: "top" }}>
                <p style={labelStyle}>Full Name</p>
                <p style={valueStyle}>{candidate?.full_name}</p>
              </td>
              <td style={{ padding: "12px 0 12px 20px", width: "50%", verticalAlign: "top" }}>
                <p style={labelStyle}>Evaluation Date</p>
                <p style={valueStyle}>{evalDate}</p>
              </td>
            </tr>
            <tr>
              <td style={{ padding: "12px 20px 12px 0", verticalAlign: "top" }}>
                <p style={labelStyle}>Current Position</p>
                <p style={valueStyle}>{candidate?.current_role || "Not Specified"}</p>
              </td>
              <td style={{ padding: "12px 0 12px 20px", verticalAlign: "top" }}>
                <p style={labelStyle}>Current Employer</p>
                <p style={valueStyle}>{candidate?.current_employer || "Not Specified"}</p>
              </td>
            </tr>
            <tr>
              <td style={{ padding: "12px 20px 12px 0", verticalAlign: "top" }}>
                <p style={labelStyle}>Years of Experience</p>
                <p style={valueStyle}>{candidate?.years_experience ? `${candidate.years_experience} Years` : "Not Specified"}</p>
              </td>
              <td style={{ padding: "12px 0 12px 20px", verticalAlign: "top" }}>
                <p style={labelStyle}>Email Address</p>
                <p style={valueStyle}>{candidate?.email || "Not Specified"}</p>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Proposed Award */}
        <h2 style={sectionTitle}>Proposed Award</h2>
        <div style={{ backgroundColor: "#eff6ff", border: "2px solid #1e3a5f", borderRadius: "8px", padding: "20px", textAlign: "center" }}>
          <p style={{ fontSize: "14pt", fontWeight: "bold", color: "#1e3a5f", margin: "0 0 10px 0" }}>{proposedAward}</p>
          <p style={{ fontSize: "12pt", color: "#333", margin: "0" }}>
            {level.charAt(0).toUpperCase() + level.slice(1)} Level  |  {credits} Credits
          </p>
        </div>

        {/* Documents Reviewed */}
        <h2 style={sectionTitle}>Documents Reviewed</h2>
        <ul style={{ fontSize: "11pt", margin: "0", paddingLeft: "25px", lineHeight: "1.8" }}>
          {docsList.map((doc, i) => (
            <li key={i}>{doc.category || doc.file_name}</li>
          ))}
        </ul>

        <div style={{ flexGrow: 1 }}></div>
        <PageFooter candidate={candidate} evalDate={evalDate} pageNum={1} totalPages={3} institution={institution} />
      </div>

      {/* PAGE 2 - Evaluation Summary & Competencies */}
      <div data-report-page="ipcb-2" style={{ ...pageStyle, pageBreakAfter: "always" }}>
        <PageHeader ipcb={ipcb} pageNum={2} totalPages={3} />

        {/* Summary of Evaluation */}
        <h2 style={sectionTitle}>Summary of Evaluation</h2>
        <p style={{ fontSize: "12pt", textAlign: "justify", lineHeight: "1.8", margin: "0 0 20px 0" }}>
          {relevanceSummary}
        </p>

        {/* Assessor Comments */}
        <h2 style={sectionTitle}>Assessor Comments</h2>
        <p style={{ fontSize: "12pt", textAlign: "justify", lineHeight: "1.8", margin: "0 0 20px 0" }}>
          {assessorComments}
        </p>

        {/* Credit Recommendation */}
        <h2 style={sectionTitle}>Credit Award Recommendation</h2>
        <p style={{ fontSize: "12pt", lineHeight: "1.8", margin: "0 0 10px 0" }}>
          Based on the RPL assessment framework for <strong>{level.charAt(0).toUpperCase() + level.slice(1)}</strong> level qualifications, the recommended credit award is:
        </p>
        <p style={{ fontSize: "24pt", fontWeight: "bold", color: "#1e3a5f", textAlign: "center", margin: "15px 0 25px 0" }}>
          {credits} Credits
        </p>

        {/* Competencies Demonstrated */}
        <h2 style={sectionTitle}>Competencies Demonstrated</h2>
        <p style={{ fontSize: "12pt", lineHeight: "1.6", margin: "0 0 10px 0" }}>
          Based on evidence reviewed, the candidate has demonstrated proficiency in the following areas:
        </p>
        <ol style={{ fontSize: "11pt", paddingLeft: "25px", lineHeight: "1.8", margin: "0" }}>
          {competenciesList.map((comp, i) => (
            <li key={i} style={{ marginBottom: "8px" }}>{comp}</li>
          ))}
        </ol>

        <div style={{ flexGrow: 1 }}></div>
        <PageFooter candidate={candidate} evalDate={evalDate} pageNum={2} totalPages={3} institution={institution} />
      </div>

      {/* PAGE 3 - Acknowledgement & Signatures */}
      <div data-report-page="ipcb-3" style={pageStyle}>
        <PageHeader ipcb={ipcb} pageNum={3} totalPages={3} />

        {/* Candidate Acknowledgement */}
        <h2 style={sectionTitle}>Candidate Acknowledgement</h2>
        <p style={{ fontSize: "12pt", textAlign: "justify", lineHeight: "1.8", margin: "0 0 15px 0" }}>
          I, <strong>{candidate?.full_name}</strong>, hereby acknowledge receipt of this RPL Evaluation Report 
          prepared by the <strong>{formatOrgName(ipcb, "Awarding Body")}</strong>.
        </p>
        <p style={{ fontSize: "12pt", textAlign: "justify", lineHeight: "1.8", margin: "0 0 15px 0" }}>
          I confirm that I have reviewed and understood the contents of this report, including the competencies 
          assessed and the proposed award recommendation of <strong>{proposedAward}</strong>.
        </p>
        <p style={{ fontSize: "12pt", textAlign: "justify", lineHeight: "1.8", margin: "0 0 30px 0" }}>
          I accept the evaluation outcome and agree to proceed with the conferment process in collaboration 
          with <strong>{formatOrgName(institution, "the Awarding Institution")}</strong>, as per the established 
          procedures and guidelines for Recognition of Prior Learning (RPL).
        </p>

        {/* Signatures */}
        <h2 style={sectionTitle}>Signatures</h2>
        <table style={{ width: "100%", marginTop: "15px" }}>
          <tbody>
            <tr>
              <td style={{ width: "45%", verticalAlign: "top", paddingRight: "40px" }}>
                <p style={{ fontSize: "10pt", fontWeight: "bold", color: "#666", marginBottom: "10px", textTransform: "uppercase" }}>Candidate Signature</p>
                <div style={{ borderBottom: "2px solid #1a1a1a", height: "60px", marginBottom: "10px" }}></div>
                <p style={{ fontSize: "12pt", margin: "0 0 5px 0" }}>{candidate?.full_name}</p>
                <p style={{ fontSize: "11pt", color: "#666", margin: "0" }}>Date: ________________________________</p>
              </td>
              <td style={{ width: "10%" }}></td>
              <td style={{ width: "45%", verticalAlign: "top", paddingLeft: "40px" }}>
                <p style={{ fontSize: "10pt", fontWeight: "bold", color: "#666", marginBottom: "10px", textTransform: "uppercase" }}>IPCB Representative</p>
                <div style={{ borderBottom: "2px solid #1a1a1a", height: "60px", marginBottom: "10px" }}></div>
                <p style={{ fontSize: "12pt", margin: "0 0 5px 0" }}>Authorized Signatory</p>
                <p style={{ fontSize: "11pt", color: "#666", margin: "0" }}>Date: ________________________________</p>
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ flexGrow: 1 }}></div>

        {candidate?.ipcb_report_id && (
          <div style={{
            display: "flex", alignItems: "center", gap: "24px",
            padding: "16px", border: "1px solid #cbd5e1", borderRadius: "8px",
            backgroundColor: "#f8fafc", marginTop: "20px",
          }}>
            <div style={{ background: "#ffffff", padding: "8px", borderRadius: "6px", border: "1px solid #e2e8f0", lineHeight: 0 }}>
              <QRCodeCanvas
                value={`${VERIFY_BASE}/verify/${candidate.ipcb_report_id}`}
                size={140}
                level="H"
                includeMargin={false}
              />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: "11pt", fontWeight: "bold", color: "#1e3a5f", margin: "0 0 6px 0" }}>
                Verify this report online
              </p>
              <p style={{ fontSize: "10pt", color: "#475569", margin: "0", lineHeight: "1.5" }}>
                Scan the QR code with any phone camera, or visit <strong>{VERIFY_BASE.replace(/^https?:\/\//, "")}/verify</strong> and paste the Report ID below.
              </p>
              <p style={{ fontSize: "13pt", color: "#1e3a5f", margin: "10px 0 0 0", fontFamily: "Courier New, monospace", fontWeight: "bold", letterSpacing: "1px" }}>
                {candidate.ipcb_report_id}
              </p>
            </div>
          </div>
        )}

        <PageFooter candidate={candidate} evalDate={evalDate} pageNum={3} totalPages={3} institution={institution} />
      </div>
    </div>
  );
});

IPCBReport.displayName = "IPCBReport";
export default IPCBReport;
